//
// Copyright 2025 DXOS.org
//

import { BaseError, type BaseErrorOptions } from '@dxos/errors';

/**
 * Stable code (error name) for {@link RunAgainError}. Exported so trace consumers can recognize a
 * run-again yield from a persisted `operation.end` event via its `errorCode` field, without matching
 * on the human-readable message.
 */
export const RUN_AGAIN_ERROR_CODE = 'RunAgainError';

/**
 * Message carried by {@link RunAgainError}. Exported for backward compatibility: `operation.end`
 * events persisted before {@link RUN_AGAIN_ERROR_CODE} existed carry only this message, so trace
 * consumers fall back to it when `errorCode` is absent.
 */
export const RUN_AGAIN_MESSAGE = 'Run again';

/**
 * Raised when a function wants to yield to the scheduler to not exceed platform limits.
 * The scheduler will re-run the function after the yield.
 * It is assumed that the function can be retried with the same input.
 * The function might have produced side-effects on the first run.
 */
export class RunAgainError extends BaseError.extend(RUN_AGAIN_ERROR_CODE, RUN_AGAIN_MESSAGE) {}

// Errors from @dxos/operation.

export class InvokerNotInitializedError extends BaseError.extend(
  'InvokerNotInitializedError',
  'Invoker not initialized',
) {}

export class NoHandlerError extends BaseError.extend('NoHandlerError', 'No handler found for operation. ') {
  constructor(operationKey: string) {
    super({ context: { operationKey } });
  }
}

// Errors from @dxos/functions.

export class ServiceNotAvailableError extends BaseError.extend('ServiceNotAvailable', 'Service not available') {
  constructor(service: string, options?: Omit<BaseErrorOptions, 'context'>) {
    super({ context: { service }, message: `Service not available: ${service}`, ...options });
  }
}

export class FunctionNotFoundError extends BaseError.extend('FunctionNotFound', 'Function not found') {
  constructor(functionKey: string, options?: Omit<BaseErrorOptions, 'context'>) {
    super({ context: { function: functionKey }, ...options });
  }
}

export class FunctionError extends BaseError.extend('FunctionError', 'Function invocation error') {}

export class InvalidOperationInputError extends BaseError.extend(
  'InvalidOperationInput',
  'Operation input did not match schema',
) {}

export class InvalidOperationOutputError extends BaseError.extend(
  'InvalidOperationOutput',
  'Operation output did not match schema',
) {}

export class TriggerStateNotFoundError extends BaseError.extend('TriggerStateNotFound', 'Trigger state not found') {}

/**
 * Raised when the upstream AI gateway responds with a structured JSON error envelope
 * (`{ "type": "error", "error": { "type": ..., "message": ... } }`). Surfaces as a typed
 * defect from `FunctionsAiHttpClient`, bypassing `@effect/ai`'s generic `HttpResponseError`
 * wrapping so callers can match on the error class directly.
 */
export class FunctionsAiUpstreamError extends BaseError.extend(
  'FunctionsAiUpstreamError',
  'Upstream AI service returned an error',
) {}

/**
 * Specialized `FunctionsAiUpstreamError` for the memoization layer: the recorded fixture for a
 * given cache key was not found and `DX_UPDATE_MODEL_FIXTURES` is unset. The `cacheKey` is exposed
 * via `context.cacheKey` to make regeneration straightforward.
 */
export class FunctionsAiMemoizationMissError extends BaseError.extend(
  'FunctionsAiMemoizationMissError',
  'No memoized AI conversation found for the given cache key',
) {}
